import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api.ts';
import { Badge, Problem } from './bits.tsx';

/**
 * WHAT THIS SERVER IS, without asking anybody else.
 *
 * Every figure here is read from disk or from memory already held. The
 * temptation is to also check whether Xtream is answering and whether Plex is
 * up — and that is precisely the screen that hangs for thirty seconds on the
 * evening something is down, which is the only evening anybody opens it.
 * "Configured" is a fact we own; "reachable" is a question, and a question
 * belongs behind a button somebody presses on purpose.
 */
export function ServerPanel() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'server'],
    queryFn: () => api.adminServer(),
    staleTime: 30_000,
  });

  if (isLoading) return <div className="muted center">Reading…</div>;
  if (error) return <Problem error={error} />;
  if (!data) return null;

  return (
    <>
      <p className="muted admin-note">
        Read-only, and nothing here reaches out of the house — so it answers
        instantly on the evening something is broken, which is the only evening
        this page gets opened.
      </p>

      <div className="stat-grid">
        <Stat label="Version" value={data.version} />
        <Stat label="Node" value={data.node} />
        <Stat label="Up for" value={uptime(data.uptimeSec)} />
        <Stat label="Channels" value={`${data.channels.enabled} of ${data.channels.total}`}
          sub={`${data.channels.tv} TV · ${data.channels.radio} radio`} />
        <Stat label="Accounts" value={String(data.users.total)}
          sub={`${data.users.admins} admin${data.users.admins === 1 ? '' : 's'}`} />
        <Stat label="Guide cached for" value={`${data.guide.ttlHours}h`} />
        <Stat
          label="Sync rules run"
          value={`${String(data.autoSync.hour).padStart(2, '0')}:${String(data.autoSync.minute).padStart(2, '0')}`}
          sub="daily, server time"
        />
        <Stat
          label="Plex"
          value={data.plex.pinned ? 'pinned' : 'not pinned'}
          sub={data.plex.serverUrl ?? 'discovered'}
        />
        {/* The only thing here that rewrites media bytes, so the only one
            worth suspecting when a channel that used to play stops. */}
        <Stat
          label="Audio relabelling"
          value={data.audioFix.enabled ? 'on' : 'off'}
          sub={data.audioFix.enabled
            ? `${data.audioFix.framesFixed.toLocaleString()} frames corrected`
            : 'disabled by N8MEDIA_NO_AUDIO_FIX'}
        />
      </div>

      <section className="admin-section">
        <h3 className="admin-subhead">Sources</h3>
        <p className="muted admin-note">
          A KIND of source, not an account — several accounts can share one. A source
          with no credentials is inert rather than broken: nothing points at it.
        </p>
        <table className="admin-table">
          <thead>
            <tr><th>Source</th><th>State</th><th>Channels</th><th>Available</th></tr>
          </thead>
          <tbody>
            {data.sources.map((s) => (
              <tr key={s.id}>
                <td><span className="admin-name">{s.name}</span></td>
                <td>
                  {s.configured
                    ? <Badge tone="good">configured</Badge>
                    : <Badge tone="warn">no credentials</Badge>}
                </td>
                <td>{s.channels}</td>
                <td>{s.enabled}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="muted stat-sub">{sub}</div>}
    </div>
  );
}

function uptime(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const mins = Math.floor(sec / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ${mins % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}
